import { useState, useEffect } from 'react';
import { ideasApi, matchingApi } from '../lib/api';
import IdeaSelector from '../components/matching/IdeaSelector';
import MatchScoreCard from '../components/matching/MatchScoreCard';
import CompanyDetail from '../components/matching/CompanyDetail';

export default function CompanyMatching({ onBuildPitch }) {
  const [ideas, setIdeas] = useState([]);
  const [selectedIdeaId, setSelectedIdeaId] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matching, setMatching] = useState(false);
  const [error, setError] = useState('');
  const [usage, setUsage] = useState(null);
  const [viewingCompanyId, setViewingCompanyId] = useState(null);

  useEffect(() => {
    loadIdeas();
  }, []);

  const loadIdeas = async () => {
    try {
      const data = await ideasApi.list({ status: '' }); // all non-archived
      setIdeas((data.ideas || []).filter(i => i.status !== 'archived'));
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleSelectIdea = async (ideaId) => {
    setSelectedIdeaId(ideaId);
    setMatches([]);
    setError('');

    // Load cached matches
    try {
      const data = await matchingApi.getCached(ideaId);
      if (data.matches?.length > 0) {
        setMatches(data.matches);
      }
    } catch (err) {
      // No cached matches, that's fine
    }
  };

  const handleRunMatch = async () => {
    if (!selectedIdeaId) return;
    setMatching(true);
    setError('');
    try {
      const data = await matchingApi.run(selectedIdeaId);
      setMatches(data.matches || []);
      setUsage(data.usage);
    } catch (err) {
      setError(err.message);
    }
    setMatching(false);
  };

  // Company detail view
  if (viewingCompanyId) {
    return (
      <CompanyDetail
        companyId={viewingCompanyId}
        onBack={() => setViewingCompanyId(null)}
      />
    );
  }

  if (loading) return <div className="text-center py-16 text-gray-400">Loading...</div>;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-vault-navy">Company Matching</h1>
        <p className="text-gray-500 text-sm mt-1">
          AI scores your ideas against toy company wishlists to find the best fits
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: Idea selector */}
        <div className="lg:col-span-4">
          <div className="bg-white rounded-xl border border-vault-border p-5">
            <IdeaSelector
              ideas={ideas}
              selectedId={selectedIdeaId}
              onSelect={handleSelectIdea}
            />

            {selectedIdeaId && (
              <div className="mt-4 pt-4 border-t border-vault-border">
                <button
                  onClick={handleRunMatch}
                  disabled={matching}
                  className="w-full py-2.5 bg-vault-gold hover:bg-vault-gold-dark text-white font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {matching ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Matching...
                    </>
                  ) : (
                    'Run AI Match'
                  )}
                </button>

                {usage && (
                  <p className="text-xs text-gray-400 text-center mt-2">
                    {usage.used} / {usage.limit} matches used today
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right: Results */}
        <div className="lg:col-span-8">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 text-vault-error text-sm">{error}</div>
          )}

          {!selectedIdeaId ? (
            <div className="text-center py-16">
              <div className="text-5xl mb-4 opacity-30">&#127919;</div>
              <h3 className="text-lg font-semibold text-vault-navy mb-2">Select an idea</h3>
              <p className="text-gray-500 text-sm">Choose an idea from your vault to find matching companies</p>
            </div>
          ) : matches.length === 0 && !matching ? (
            <div className="text-center py-16">
              <div className="text-5xl mb-4 opacity-30">&#128269;</div>
              <h3 className="text-lg font-semibold text-vault-navy mb-2">No matches yet</h3>
              <p className="text-gray-500 text-sm">Click "Run AI Match" to score this idea against all companies</p>
            </div>
          ) : matching ? (
            <div className="text-center py-16">
              <svg className="animate-spin w-8 h-8 text-vault-gold mx-auto mb-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <h3 className="text-lg font-semibold text-vault-navy mb-2">Analyzing matches...</h3>
              <p className="text-gray-500 text-sm">AI is scoring your idea against company wishlists and your meeting notes</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-vault-navy">
                  {matches.length} Match{matches.length !== 1 ? 'es' : ''}
                </h2>
                {matches[0]?.generated_at && (
                  <span className="text-xs text-gray-400">
                    Last run: {new Date(matches[0].generated_at).toLocaleString()}
                  </span>
                )}
              </div>
              {matches.map(match => (
                <MatchScoreCard
                  key={match.company_id}
                  match={match}
                  onViewCompany={() => setViewingCompanyId(match.company_id)}
                  onBuildPitch={() => {
                    onBuildPitch?.(selectedIdeaId, match.company_id);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
